import React from 'react';
import { Users, Mail, Phone, Calendar, MapPin, Globe, AlertCircle } from 'lucide-react';
import StatusControls from '../StatusControls';

const OverviewTab = ({ 
  vendor, 
  vendorProfile, 
  vendorSubscriptions, 
  timeRemaining, 
  formatDate,
  onToggleFeatured,
  onToggleActive,
  isUpdatingFeatured,
  isUpdatingActive
}) => {
  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Basic Information */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">Basic Information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                <Users className="w-4 h-4 text-gray-600" />
              </div>
              <div className="min-w-0 flex-1">
                <dt className="text-sm font-medium text-gray-500 mb-1">Full Name</dt>
                <dd className="text-sm font-semibold text-gray-900">
                  {vendor?.name || 'N/A'}
                </dd>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                <Mail className="w-4 h-4 text-gray-600" />
              </div>
              <div className="min-w-0 flex-1">
                <dt className="text-sm font-medium text-gray-500 mb-1">Email Address</dt>
                <dd className="text-sm text-gray-900 break-all">
                  {vendor?.email}
                </dd>
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                <Phone className="w-4 h-4 text-gray-600" />
              </div>
              <div className="min-w-0 flex-1">
                <dt className="text-sm font-medium text-gray-500 mb-1">Phone Number</dt>
                <dd className="text-sm text-gray-900">
                  {vendor?.phone || 'Not provided'}
                </dd>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-4 h-4 text-gray-600" />
              </div>
              <div className="min-w-0 flex-1">
                <dt className="text-sm font-medium text-gray-500 mb-1">Joined Date</dt>
                <dd className="text-sm text-gray-900">
                  {formatDate(vendor?.created_at)}
                </dd>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Business Information */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">Business Information</h3>
        {vendorProfile ? (
          <div className="space-y-6">
            {/* Business Details Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Users className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <dt className="text-sm font-medium text-gray-500 mb-1">Business Name</dt>
                    <dd className="text-sm font-semibold text-gray-900">
                      {vendorProfile.business_name || 'Not provided'}
                    </dd>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                    <Phone className="w-4 h-4 text-gray-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <dt className="text-sm font-medium text-gray-500 mb-1">Business Phone</dt>
                    <dd className="text-sm text-gray-900">
                      {vendorProfile.business_phone || 'Not provided'}
                    </dd>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                    <Globe className="w-4 h-4 text-gray-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <dt className="text-sm font-medium text-gray-500 mb-1">Website</dt>
                    <dd className="text-sm text-gray-900">
                      {vendorProfile.website ? (
                        <a href={vendorProfile.website} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 break-all">
                          {vendorProfile.website}
                        </a>
                      ) : 'Not provided'}
                    </dd>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                    <MapPin className="w-4 h-4 text-gray-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <dt className="text-sm font-medium text-gray-500 mb-1">Address</dt>
                    <dd className="text-sm text-gray-900">
                      {vendorProfile.address || 'Not provided'}
                    </dd>
                  </div>
                </div>
              </div>
            </div>

            {/* Location Details Row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-gray-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <dt className="text-sm font-medium text-gray-500 mb-1">City, State</dt>
                  <dd className="text-sm text-gray-900">
                    {vendorProfile.city && vendorProfile.state ? `${vendorProfile.city}, ${vendorProfile.state}` : 'Not provided'}
                  </dd>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-gray-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <dt className="text-sm font-medium text-gray-500 mb-1">Zip Code</dt>
                  <dd className="text-sm text-gray-900">
                    {vendorProfile.zip_code || 'Not provided'}
                  </dd>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-gray-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <dt className="text-sm font-medium text-gray-500 mb-1">Country</dt>
                  <dd className="text-sm text-gray-900">
                    {vendorProfile.country || 'Not provided'}
                  </dd>
                </div>
              </div>
            </div>

            {/* Service Categories */}
            <div>
              <dt className="text-sm font-medium text-gray-500 mb-3">Service Categories</dt>
              <dd className="text-sm text-gray-900">
                {vendorProfile.vendor_categories?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {vendorProfile.vendor_categories.map((category, index) => (
                      <span key={index} className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200">
                        {category.service_category?.category_name || 'Unknown Category'}
                        {category.is_primary && (
                          <span className="ml-2 px-2 py-0.5 text-xs bg-blue-200 text-blue-700 rounded-full">
                            Primary
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-gray-500">Not provided</span>
                )}
              </dd>
            </div>

            {/* Business Stats Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Users className="w-4 h-4 text-gray-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <dt className="text-sm font-medium text-gray-500 mb-1">Team Size</dt>
                  <dd className="text-sm text-gray-900">
                    {vendorProfile.team_size || 'Not provided'}
                  </dd>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Calendar className="w-4 h-4 text-gray-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <dt className="text-sm font-medium text-gray-500 mb-1">Years Experience</dt>
                  <dd className="text-sm text-gray-900">
                    {vendorProfile.years_experience || 'Not provided'}
                  </dd>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-500 mb-2">Business profile not uploaded</p>
            <p className="text-sm text-gray-400">This vendor has not completed their business profile setup yet.</p>
          </div>
        )}
      </div>

      {/* Business Description */}
      {vendorProfile && (
        <div>
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">Business Description</h3>
          <p className="text-sm text-gray-700 bg-gray-50 p-4 rounded-lg">
            {vendorProfile.description || 'No description provided'}
          </p>
        </div>
      )}

      {/* Subscription Information */}
      {vendorSubscriptions.length > 0 && (
        <div>
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">Subscription Information</h3>
          <div className="bg-gray-50 rounded-lg p-4">
            {vendorSubscriptions.map((subscription, index) => (
              <div key={index} className="border-b border-gray-200 pb-4 last:border-b-0 last:pb-0">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-gray-900">
                    {subscription.subscription?.name || 'Unknown Plan'}
                  </h4>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    subscription.status === 'active' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {subscription.status}
                  </span>
                </div>
                
                {/* Time Counter */}
                {subscription.status === 'active' && (
                  <div className="mb-4 p-3 bg-white rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <h5 className="text-sm font-medium text-gray-900">Time Remaining</h5>
                      <Calendar className="w-4 h-4 text-gray-400" />
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900">{timeRemaining.days}</div>
                        <div className="text-xs text-gray-500">Days</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900">{timeRemaining.hours}</div>
                        <div className="text-xs text-gray-500">Hours</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900">{timeRemaining.minutes}</div>
                        <div className="text-xs text-gray-500">Min</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900">{timeRemaining.seconds}</div>
                        <div className="text-xs text-gray-500">Sec</div>
                      </div>
                    </div>
                    <div className="mt-2 text-center">
                      <span className="text-sm text-gray-600">
                        Expires on {formatDate(subscription.end_date)}
                      </span>
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-600">
                  <div>
                    <span className="font-medium">Price:</span> ${subscription.subscription?.price || 'N/A'} {subscription.subscription?.currency || 'USD'}
                  </div>
                  <div>
                    <span className="font-medium">Duration:</span> {subscription.subscription?.duration_days || 'N/A'} days
                  </div>
                  <div>
                    <span className="font-medium">Start Date:</span> {formatDate(subscription.start_date)}
                  </div>
                  <div>
                    <span className="font-medium">End Date:</span> {formatDate(subscription.end_date)}
                  </div>
                  <div>
                    <span className="font-medium">Auto Renew:</span> {subscription.auto_renew ? 'Yes' : 'No'}
                  </div>
                  <div>
                    <span className="font-medium">Payment Method:</span> {subscription.payment_method || 'N/A'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Status Controls */}
      <StatusControls
        vendorProfile={vendorProfile}
        vendor={vendor}
        onToggleFeatured={onToggleFeatured}
        onToggleActive={onToggleActive}
        isUpdatingFeatured={isUpdatingFeatured}
        isUpdatingActive={isUpdatingActive}
      />
    </div>
  );
};

export default OverviewTab;

